import { SearchableSelect } from "@/components/ui/searchable-select";
import { ApplyFromUrl } from "@/lib/openWithConfig";
import { dateLocale } from "@/lib/dateTime";
import { ListEmpty } from "@/components/patterns";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { PreviewButton } from "@/components/preview/PreviewButton";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { InlineCell } from "@/components/inline/InlineCell";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Edit, Trash2, Upload } from "lucide-react";
import { INDIAN_STATES, stateFromGstin } from "@/lib/indianStates";
import type { AccountRecord, MasterLookupRecord } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { FilterBar, type ColumnOption } from "@/components/filters/FilterBar";
import { OwnerCell } from "@/components/inline/OwnerCell";
import { CsvImportDialog, type ImportField } from "@/components/import/CsvImportDialog";
import { BulkEditDialog, type BulkField } from "@/components/inline/BulkEditDialog";
import { BulkBar, BulkButton, PageHeader, Person, ViewsPanel } from "@/components/patterns";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { useFieldFilters, type FilterFieldDef } from "@/lib/useFieldFilters";
import { useSort } from "@/lib/useSort";
import { usePersistentColumns } from "@/lib/usePersistentColumns";
import { downloadCsv, fetchAllRows } from "@/lib/exportCsv";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, valueOptionsFromFacets, type OwnerFacet } from "@/lib/filters";
import { cn, formatINRCompact } from "@/lib/utils";
import {
  CalendarDays as FilterDateIcon,
  Factory as FilterIndustryIcon,
  MapPin as FilterStateIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const ACCOUNT_FILTER_DEFAULTS = { owner: "all", industry: "all", state: "all", created: "any" };

/** An account row as the list returns it, with the rollups the table shows */
type AccountRow = AccountRecord & {
  open_value?: number;
  open_deals?: number;
  won_year?: number;
  contact_count?: number;
  last_activity_on?: string | null;
  last_activity_what?: string | null;
};

type AccountStats = {
  counts: Record<string, number>;
  open_value: number;
  open_deals: number;
  won_year: number;
  won_last_year_to_date: number;
  active_this_month: number;
  quiet_open_value: number;
};

const ACCOUNT_COLUMNS: ColumnOption[] = [
  { key: "account", label: "Account", locked: true },
  { key: "location", label: "Location" },
  { key: "owner", label: "Owner" },
  { key: "pipeline", label: "Open pipeline" },
  { key: "won", label: "Won this year" },
  { key: "contacts", label: "Contacts" },
  { key: "last", label: "Last activity" },
  { key: "actions", label: "Actions", locked: true },
];
const ACCOUNT_COLUMN_KEYS = ACCOUNT_COLUMNS.map((c) => c.key);

const QUIET_MS = 30 * 24 * 3600 * 1000;

/** "Today", "Yesterday", "12 Oct", "3 Sep 2025" */
function dayLabel(iso?: string | null) {
  if (!iso) return "No activity yet";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "success" | "warning" }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-border bg-card px-3.5 py-2.5">
      <span className="text-[11.5px] text-muted-foreground">{label}</span>
      <span className="font-mono text-xl font-semibold text-foreground">{value}</span>
      <span className={cn("text-[11.5px]", tone === "success" ? "text-success" : tone === "warning" ? "text-warning" : "text-muted-foreground")}>{sub}</span>
    </div>
  );
}

const ACCOUNT_IMPORT_FIELDS: ImportField[] = [
  { key: "name", label: "Account name", required: true, aliases: ["account", "company", "company_name", "customer", "name"] },
  { key: "industry", label: "Industry" },
  { key: "website", label: "Website", aliases: ["url", "site"] },
  { key: "email", label: "Email", check: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Email isn't valid") },
  { key: "phone", label: "Phone", aliases: ["mobile", "telephone"] },
  { key: "state", label: "State" },
  { key: "gstin", label: "GSTIN", aliases: ["gst", "gst_number"], check: (v) => (/^[0-9A-Za-z]15$/.test(v) ? null : "GSTIN should be 15 characters") },
  { key: "billing_address", label: "Billing address", aliases: ["address"] },
];

const ACCOUNT_BULK_FIELDS: BulkField[] = [
  { field: "industry", label: "Industry", kind: "text", nullable: true },
  { field: "state", label: "State", kind: "text", nullable: true },
  { field: "phone", label: "Phone", kind: "phone", nullable: true },
  { field: "website", label: "Website", kind: "text", nullable: true },
];

export function AccountsPage() {
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [view, setView] = useState("all");
  const [stats, setStats] = useState<AccountStats | null>(null);
  const columns = usePersistentColumns("accounts", ACCOUNT_COLUMN_KEYS);
  const colHidden = (k: string) => (columns.has(k) ? "" : "hidden");

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<AccountRecord | null>(null);
  const [deleteAccount, setDeleteAccount] = useState<AccountRecord | null>(null);

  // Selection and bulk actions
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOwnerOpen, setBulkOwnerOpen] = useState(false);
  const [bulkOwnerId, setBulkOwnerId] = useState("");
  const [assignable, setAssignable] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeKeep, setMergeKeep] = useState("");
  const [exporting, setExporting] = useState(false);

  // Form State
  const [form, setForm] = useState({
    name: "",
    industry: "",
    website: "",
    email: "",
    phone: "",
    billing_address: "",
    shipping_address: "",
    state: "",
    gstin: "",
    payment_term_id: "",
    price_list_id: "",
    credit_limit: "",
    notes: "",
  });
  const [priceLists, setPriceLists] = useState<{ id: string; name: string; usable_today: boolean }[]>([]);
  const [paymentTerms, setPaymentTerms] = useState<{ id: string; name: string; label: string }[]>([]);

  const [industries, setIndustries] = useState<MasterLookupRecord[]>([]);

  const { success, error } = useToast();
  const { user, hasPermission } = useAuth();
  const canEditInline = hasPermission("account.update");
  const canUpdate = hasPermission("account.update");
  const canDelete = hasPermission("account.delete");
  const canMerge = hasPermission("account.merge");
  const accountFilters = usePersistentFilters("accounts", ACCOUNT_FILTER_DEFAULTS);

  // "+ Add filter" on any field the API offers
  const fieldFilters = useFieldFilters("accounts");
  const sorting = useSort("accounts");
  const [fieldDefs, setFieldDefs] = useState<FilterFieldDef[] | null>(null);
  useEffect(() => {
    api.get("/api/Account/FilterFields").then((res) => setFieldDefs(unwrapArray<FilterFieldDef>(res))).catch(() => setFieldDefs([]));
  }, []);
  const [accountOptions, setAccountOptions] = useState<{
    total: number;
    owners: OwnerFacet[];
    industries: { value: string; count: number }[];
    states: { value: string; count: number }[];
  }>({ total: 0, owners: [], industries: [], states: [] });

  const filterQuery = () => {
    const f = accountFilters.values;
    return toQuery({
      search: search.trim(),
      view: view === "all" ? "" : view,
      owner: f.owner,
      industry: f.industry,
      state: f.state,
      ...dateRangeParams(f.created, "created"),
      f: fieldFilters.param(fieldDefs), sort: sorting.sort,
    });
  };

  const loadStats = () => api.get("/api/Account/Stats").then((r) => setStats(unwrapObject(r))).catch(() => undefined);
  useEffect(() => {
    loadStats();
  }, []);

  const list = usePagedList<AccountRow>({
    deps: [accountFilters.values, fieldFilters.values, fieldDefs, search, view, sorting.sort],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const query = filterQuery();
      try {
        const join = query ? "&" : "?";
        const [accRes, indRes, optRes, termRes, plRes] = await Promise.all([
          api.get(`/api/Account/List${query}${join}page=${page}&page_size=30`),
          api.get("/api/MasterLookup/ByCategory/INDUSTRY"),
          api.get("/api/Account/FilterOptions").catch(() => null),
          api.get("/api/PaymentTerm/List").catch(() => null),
          api.get("/api/PriceList/List").catch(() => null),
        ]);
        if (termRes) setPaymentTerms(unwrapArray(termRes));
        if (plRes) setPriceLists(unwrapArray<any>(plRes));
        if (optRes) setAccountOptions(unwrapObject(optRes));
        setIndustries(unwrapArray<MasterLookupRecord>(indRes));
        return unwrapPage<AccountRow>(accRes);
      } catch (err: any) {
        throw err;
      }
    },
  });
  const accounts = list.rows;
  const loading = list.loading;
  const loadAccounts = () => {
    list.reload();
    loadStats();
  };

  useEffect(() => setSelected(new Set()), [view, search, accountFilters.values]);

  const topIndustries = useMemo(() => [...accountOptions.industries].sort((a, b) => b.count - a.count).slice(0, 5), [accountOptions.industries]);

  const blankForm = {
    name: "",
    industry: "",
    website: "",
    email: "",
    phone: "",
    billing_address: "",
    shipping_address: "",
    state: "",
    gstin: "",
    payment_term_id: "",
    price_list_id: "",
    credit_limit: "",
    notes: "",
  };
  const openAdd = () => {
    setForm(blankForm);
    setAddOpen(true);
  };

  const openEdit = (a: AccountRecord) => {
    setEditAccount(a);
    setForm({
      name: a.name || "",
      industry: a.industry || "",
      website: a.website || "",
      email: a.email || "",
      phone: a.phone || "",
      billing_address: a.billing_address || "",
      shipping_address: a.shipping_address || "",
      state: a.state || "",
      gstin: a.gstin || "",
      payment_term_id: a.payment_term_id || "",
      price_list_id: a.price_list_id || "",
      credit_limit: a.credit_limit === null || a.credit_limit === undefined ? "" : String(Number(a.credit_limit)),
      notes: a.notes || "",
    });
  };

  // Usual payment terms go with the form; the credit limit only when the person may set it
  const savePayload = () => {
    const { credit_limit, payment_term_id, price_list_id, ...rest } = form;
    return {
      ...rest,
      payment_term_id: payment_term_id || null,
      ...(hasPermission("price_list.manage") ? { price_list_id: price_list_id || null } : {}),
      ...(hasPermission("account.credit_manage") ? { credit_limit: credit_limit.trim() === "" ? null : Number(credit_limit) } : {}),
    };
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await api.post("/api/Account/Insert", savePayload());
      success("Account Created", "Company account successfully added");
      setAddOpen(false);
      loadAccounts();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create account");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editAccount) return;
    setActionLoading(true);
    try {
      await api.put(`/api/Account/Update/${editAccount.id}`, savePayload());
      success("Account Updated", "Company details saved");
      setEditAccount(null);
      loadAccounts();
    } catch (err: any) {
      error("Update Failed", err.message || "Could not update account");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteAccount) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Account/Delete/${deleteAccount.id}`);
      success("Account Deleted", "Company account removed");
      setDeleteAccount(null);
      loadAccounts();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete account");
    } finally {
      setActionLoading(false);
    }
  };

  // Selection
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allOnPage = accounts.length > 0 && accounts.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(accounts.map((a) => a.id)));
  const picked = accounts.filter((a) => selected.has(a.id));

  const openBulkOwner = () => {
    setBulkOwnerId("");
    setBulkOwnerOpen(true);
    if (!assignable.length) api.get("/api/User/Assignable?for=account").then((r) => setAssignable(unwrapArray(r))).catch(() => undefined);
  };
  const submitBulkOwner = async () => {
    setActionLoading(true);
    try {
      const r: any = await api.post("/api/Account/BulkAssign", { account_ids: Array.from(selected), owner_id: bulkOwnerId });
      success("Owner changed", `${r?.count ?? 0} account${r?.count === 1 ? "" : "s"} reassigned${r?.skipped ? `; ${r.skipped} skipped (same owner or outside your access)` : ""}`);
      setBulkOwnerOpen(false);
      setSelected(new Set());
      loadAccounts();
    } catch (err: any) {
      error("Could not reassign", err.message || "Try again");
    } finally {
      setActionLoading(false);
    }
  };

  const openMerge = () => {
    setMergeKeep(picked[0]?.id || "");
    setMergeOpen(true);
  };
  const submitMerge = async () => {
    const drop = picked.find((a) => a.id !== mergeKeep);
    if (!drop || !mergeKeep) return;
    setActionLoading(true);
    try {
      await api.post("/api/Customer/MergeAccounts", { keep_id: mergeKeep, merge_id: drop.id });
      success("Merged", `"${drop.name}" is now part of the other account.`);
      setMergeOpen(false);
      setSelected(new Set());
      loadAccounts();
    } catch (err: any) {
      error("Could not merge", err.message || "Try again");
    } finally {
      setActionLoading(false);
    }
  };

  // CSV
  const EXPORT_COLUMNS: [string, (a: AccountRow) => string | number | null | undefined][] = [
    ["Account", (a) => a.name],
    ["Industry", (a) => a.industry || ""],
    ["State", (a) => a.state || ""],
    ["GSTIN", (a) => a.gstin || ""],
    ["Email", (a) => a.email || ""],
    ["Phone", (a) => a.phone || ""],
    ["Owner", (a) => (a.owner ? `${a.owner.first_name} ${a.owner.last_name || ""}`.trim() : "")],
    ["Open pipeline", (a) => a.open_value ?? 0],
    ["Open deals", (a) => a.open_deals ?? 0],
    ["Won this year", (a) => a.won_year ?? 0],
    ["Contacts", (a) => a.contact_count ?? 0],
    ["Last activity", (a) => (a.last_activity_on ? new Date(a.last_activity_on).toLocaleDateString(dateLocale()) : "")],
    ["Created", (a) => (a.created_on ? new Date(a.created_on).toLocaleDateString(dateLocale()) : "")],
  ];
  const fileName = () => `accounts-${new Date().toISOString().slice(0, 10)}.csv`;
  const exportSelected = () => downloadCsv(fileName(), EXPORT_COLUMNS, picked);
  const exportAll = async () => {
    setExporting(true);
    try {
      const { rows, total } = await fetchAllRows<AccountRow>("/api/Account/List", filterQuery());
      downloadCsv(fileName(), EXPORT_COLUMNS, rows);
      if (total > rows.length) error("Export is partial", `The first ${rows.length} of ${total} accounts were saved. Narrow the filters to get the rest.`);
    } catch (e: any) {
      error("Could not export", e?.message || "Try again");
    } finally {
      setExporting(false);
    }
  };

  const c = stats?.counts || {};
  const wonDelta =
    stats && stats.won_last_year_to_date > 0 ? Math.round(((stats.won_year - stats.won_last_year_to_date) / stats.won_last_year_to_date) * 100) : null;

  // A saved view, or the filters Ask the CRM hands over with ?apply=
  const applySaved = (cfg: any) => {
    setView(cfg.view || "all");
    setSearch(cfg.search || "");
    accountFilters.replace(cfg.filters || {});
    fieldFilters.setAll(cfg.fieldFilters);
    sorting.setSort(cfg.sort);
  };

  return (
    <div className="space-y-4">
      <ApplyFromUrl apply={applySaved} />
      <ViewsPanel
        title="Accounts"
        subtitle="Companies you sell to"
        saved={{
          module: "accounts",
          getConfig: () => ({ view, search, filters: accountFilters.values, fieldFilters: fieldFilters.values, sort: sorting.sort }),
          apply: applySaved,
        }}
        active={view}
        onSelect={setView}
        groups={[
          {
            items: [
              { key: "all", label: "All accounts", count: c.all },
              { key: "mine", label: "My accounts", count: c.mine },
              { key: "customers", label: "Customers", count: c.customers },
              { key: "prospects", label: "Prospects", count: c.prospects },
              { key: "quiet", label: "Quiet 30+ days", count: c.quiet, tone: "warning" },
              { key: "duplicates", label: "Possible duplicates", count: c.duplicates, tone: "danger" },
            ],
          },
        ]}
        footer={
          topIndustries.length > 0 ? (
            <div className="mt-3 flex flex-col gap-1.5 px-2.5">
              <div className="pb-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">By industry</div>
              {topIndustries.map((ind) => (
                <button
                  key={ind.value}
                  type="button"
                  onClick={() => accountFilters.set("industry", accountFilters.values.industry === ind.value ? "all" : ind.value)}
                  className="flex flex-col gap-1 text-left"
                >
                  <div className="flex justify-between text-xs">
                    <span className={cn("truncate", accountFilters.values.industry === ind.value ? "font-semibold text-primary" : "text-foreground")}>{ind.value}</span>
                    <span className="font-mono text-muted-foreground">{ind.count}</span>
                  </div>
                  <div className="h-1 rounded-sm bg-border">
                    <div className="h-full rounded-sm bg-primary" style={{ width: `${Math.round((ind.count / (topIndustries[0]?.count || 1)) * 100)}%` }} />
                  </div>
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      <PageHeader
        title="Accounts"
        summary={list.total}
        actions={
          <>
            {hasPermission("account.create") && (
              <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => setImportOpen(true)}>
                Import
              </Button>
            )}
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={exportAll} disabled={exporting}>
              Export
            </Button>
            {hasPermission("account.create") && (
              <Button size="sm" data-new-record onClick={openAdd}>
                <Building2 className="size-4 mr-1.5" />
                New account
              </Button>
            )}
          </>
        }
      />

      {/* Headline numbers */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <Kpi label="Open pipeline" value={formatINRCompact(stats.open_value)} sub={`across ${stats.open_deals} deal${stats.open_deals === 1 ? "" : "s"}`} />
          <Kpi
            label="Won this year"
            value={formatINRCompact(stats.won_year)}
            sub={wonDelta === null ? "since 1 January" : `${wonDelta >= 0 ? "▲" : "▼"} ${Math.abs(wonDelta)}% vs same time last year`}
            tone={wonDelta !== null && wonDelta >= 0 ? "success" : undefined}
          />
          <Kpi label="Active this month" value={String(stats.active_this_month)} sub={c.all ? `${Math.round((stats.active_this_month / c.all) * 100)}% of accounts` : "of accounts"} />
          <Kpi
            label="Quiet 30+ days"
            value={String(c.quiet ?? 0)}
            sub={stats.quiet_open_value > 0 ? `${formatINRCompact(stats.quiet_open_value)} open with them` : "none at risk"}
            tone={c.quiet ? "warning" : undefined}
          />
        </div>
      )}

      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import accounts"
        noun="accounts"
        fields={ACCOUNT_IMPORT_FIELDS}
        create={async (r) => {
          await api.post("/api/Account/Insert", { name: r.name, industry: r.industry || undefined, website: r.website || undefined, email: r.email || undefined, phone: r.phone || undefined, state: r.state || undefined, gstin: r.gstin || undefined, billing_address: r.billing_address || undefined });
        }}
        onDone={() => list.reload()}
        hint="One account per row. Accounts you import are owned by you."
      />

      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} list="Account" ids={Array.from(selected)} fields={ACCOUNT_BULK_FIELDS} onDone={() => list.reload()} />

      <BulkBar count={selected.size} onClear={() => setSelected(new Set())}>
        {canUpdate && <BulkButton onClick={openBulkOwner}>Reassign</BulkButton>}
        {canEditInline && <BulkButton onClick={() => setBulkEditOpen(true)}>Edit a field</BulkButton>}
        {canMerge && selected.size === 2 && <BulkButton onClick={openMerge}>Merge</BulkButton>}
        <BulkButton onClick={exportSelected}>Export</BulkButton>
      </BulkBar>

      {/* Filters */}
      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search name, industry, email, phone" }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(accountOptions.owners, accountOptions.total, user?.Id), defaultValue: "all" },
          { name: "industry", label: "Industry", icon: FilterIndustryIcon, type: "select", options: valueOptionsFromFacets(accountOptions.industries, "All industries", accountOptions.total), defaultValue: "all" },
          { name: "state", label: "State", icon: FilterStateIcon, type: "select", options: valueOptionsFromFacets(accountOptions.states, "All states", accountOptions.total), defaultValue: "all" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={accountFilters.values}
        onChange={(name, value) => accountFilters.set(name as keyof typeof ACCOUNT_FILTER_DEFAULTS, value)}
        onReset={accountFilters.reset}
        quickKey="accounts"
        fieldFilters={{ fields: fieldDefs || [], value: fieldFilters.values, onChange: fieldFilters.setAll, suggestUrl: "/api/Account/FilterValues" }}
        sort={{ fields: fieldDefs || [], value: sorting.sort, onChange: sorting.setSort }}
        shown={list.total}
        total={list.total}
        noun="accounts"
        columns={{ options: ACCOUNT_COLUMNS, value: columns.visible, onChange: columns.setVisible }}
      />

      {/* Table */}
      {loading && accounts.length === 0 ? (
        <TableSkeleton />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow className="bg-panel hover:bg-panel border-b border-border">
                <TableHead className="w-10">
                  <input type="checkbox" aria-label="Select all" checked={allOnPage} onChange={toggleAll} className="rounded border-border size-4 accent-primary cursor-pointer" />
                </TableHead>
                {([
                  ["account", "Account", ""],
                  ["location", "Location", ""],
                  ["owner", "Owner", ""],
                  ["pipeline", "Open pipeline", "text-right"],
                  ["won", "Won this year", "text-right"],
                  ["contacts", "Contacts", ""],
                  ["last", "Last activity", ""],
                  ["actions", "Actions", "text-right"],
                ] as [string, string, string][]).map(([k, label, align]) => (
                  <TableHead key={k} className={cn("text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", align, colHidden(k))}>
                    {label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="p-0">
                    <ListEmpty
                      noun="accounts" error={list.error} onRetry={list.reload}
                      icon={Building2}
                      filtered={!!search.trim() || view !== "all" || accountFilters.activeCount > 0 || fieldFilters.activeCount > 0}
                      onClear={() => {
                        setSearch("");
                        setView("all");
                        accountFilters.reset();
                        fieldFilters.reset();
                      }}
                      createLabel="+ New account"
                      onCreate={hasPermission("account.create") ? openAdd : undefined}
                      firstUse="Accounts are the companies you sell to. Add one, or convert a qualified lead."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((acc) => {
                  const quiet = acc.last_activity_on ? Date.now() - new Date(acc.last_activity_on).getTime() > QUIET_MS : false;
                  return (
                    <TableRow key={acc.id} className={cn("group/row text-xs", selected.has(acc.id) && "bg-primary/5")}>
                      <TableCell>
                        <input type="checkbox" aria-label={`Select ${acc.name}`} checked={selected.has(acc.id)} onChange={() => toggle(acc.id)} className="rounded border-border size-4 accent-primary cursor-pointer" />
                      </TableCell>
                      <TableCell className={colHidden("account")}>
                        <div className="flex items-center gap-2.5">
                          <div className="size-7 rounded-lg bg-accent text-accent-foreground flex items-center justify-center font-bold text-[10.5px] shrink-0">
                            {acc.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <Link to={`/accounts/${acc.id}`} className="font-semibold text-foreground hover:text-primary hover:underline block truncate">
                              {acc.name}
                            </Link>
                            <PreviewButton entity="Account" id={acc.id} label={acc.name} className="ml-1 align-middle" />
                            <span className="text-[11.5px] text-muted-foreground">{acc.industry || "—"}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={cn("text-muted-foreground", colHidden("location"))}><InlineCell list="Account" id={acc.id} field="state" kind="text" value={acc.state} display={acc.state || "—"} enabled={canEditInline} onSaved={(row) => list.patchRow(acc.id, row)} onConflict={list.reload} />
                    </TableCell>
                      <TableCell className={colHidden("owner")}>
                        <OwnerCell list="Account" id={acc.id} ownerId={acc.owner_id} owner={acc.owner} enabled={canEditInline} onSaved={(row) => list.patchRow(acc.id, row)} onConflict={list.reload} />
                      </TableCell>
                      <TableCell className={cn("text-right", colHidden("pipeline"))}>
                        <div className="font-mono font-semibold">{acc.open_value ? formatINRCompact(acc.open_value) : "—"}</div>
                        <div className="text-[11.5px] text-muted-foreground">{acc.open_deals ? `${acc.open_deals} open deal${acc.open_deals === 1 ? "" : "s"}` : "No open deals"}</div>
                      </TableCell>
                      <TableCell className={cn("text-right font-mono", acc.won_year ? "text-success" : "text-muted-foreground", colHidden("won"))}>
                        {acc.won_year ? formatINRCompact(acc.won_year) : "—"}
                      </TableCell>
                      <TableCell className={cn("text-muted-foreground", colHidden("contacts"))}>
                        {acc.contact_count ? `${acc.contact_count} ${acc.contact_count === 1 ? "person" : "people"}` : "—"}
                      </TableCell>
                      <TableCell className={colHidden("last")}>
                        <div className={cn(quiet ? "font-semibold text-warning" : "text-foreground")}>{dayLabel(acc.last_activity_on)}</div>
                        <div className="text-[11.5px] text-muted-foreground max-w-[200px] truncate">{acc.last_activity_what || ""}</div>
                      </TableCell>
                      <TableCell className={cn("text-right space-x-1 whitespace-nowrap", colHidden("actions"))}>
                        {canUpdate && (
                          <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(acc)} className="size-8">
                            <Edit className="size-3.5" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteAccount(acc)} className="size-8 text-destructive">
                            <Trash2 className="size-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* Reassign */}
      <Dialog open={bulkOwnerOpen} onOpenChange={setBulkOwnerOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reassign {selected.size} account{selected.size === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>Their open deals and contacts keep their own owners.</DialogDescription>
          </DialogHeader>
          <SearchableSelect value={bulkOwnerId} onChange={(e) => setBulkOwnerId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Choose an owner…</option>
            {assignable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name || ""}
              </option>
            ))}
          </SearchableSelect>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOwnerOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!bulkOwnerId || actionLoading} onClick={submitBulkOwner}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge two */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Merge these two accounts</DialogTitle>
            <DialogDescription>Contacts, deals, quotes and notes move to the account you keep. The other one is removed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {picked.map((a) => (
              <label key={a.id} className={cn("flex items-start gap-3 rounded-lg border p-3 cursor-pointer", mergeKeep === a.id ? "border-primary bg-primary/5" : "border-border")}>
                <input type="radio" name="keep" checked={mergeKeep === a.id} onChange={() => setMergeKeep(a.id)} className="mt-1 accent-primary" />
                <span className="text-sm">
                  <span className="font-semibold block">Keep {a.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {a.contact_count || 0} contacts · {a.open_deals || 0} open deals · {a.gstin || "no GSTIN"}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>
              Cancel
            </Button>
            <Button disabled={actionLoading || !mergeKeep} onClick={submitMerge}>
              {actionLoading ? "Merging…" : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Dialog */}
      <Dialog open={addOpen || !!editAccount} onOpenChange={(open) => !open && (setAddOpen(false), setEditAccount(null))}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={editAccount ? handleEditSubmit : handleAddSubmit}>
            <DialogHeader>
              <DialogTitle>{editAccount ? "Edit Account" : "Add Account"}</DialogTitle>
              <DialogDescription>
                Fill in the organization contact and address specifications.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="acc_name">Company / Organization Name *</Label>
                <Input
                  id="acc_name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="acc_industry">Industry</Label>
                  <SearchableSelect
                    id="acc_industry"
                    className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 sm:px-3.5 text-xs sm:text-sm focus:ring-1 focus:ring-ring"
                    value={form.industry}
                    onChange={(e) => setForm((p) => ({ ...p, industry: e.target.value }))}
                  >
                    <option value="">-- Select Industry --</option>
                    {industries.map((ind) => (
                      <option key={ind.code} value={ind.name}>
                        {ind.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acc_website">Website</Label>
                  <Input
                    id="acc_website"
                    placeholder="e.g. company.com"
                    value={form.website}
                    onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="acc_email">Corporate Email</Label>
                  <Input
                    id="acc_email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acc_phone">Corporate Phone</Label>
                  <Input
                    id="acc_phone"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="acc_billing">Billing Address</Label>
                <Input
                  id="acc_billing"
                  value={form.billing_address}
                  onChange={(e) => setForm((p) => ({ ...p, billing_address: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="acc_gstin">GSTIN</Label>
                  <Input
                    id="acc_gstin"
                    placeholder="27AAACN1234F1Z5"
                    maxLength={15}
                    value={form.gstin}
                    onChange={(e) => {
                      const gstin = e.target.value.toUpperCase();
                      // GSTIN starts with the state code, so fill the state when it is empty
                      setForm((p) => ({ ...p, gstin, state: p.state || stateFromGstin(gstin) }));
                    }}
                    className="font-mono uppercase"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acc_state">State</Label>
                  <SearchableSelect
                    id="acc_state"
                    value={form.state}
                    onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
                    className="h-9 sm:h-10 w-full rounded-md border border-border bg-background px-2.5 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">-- Select state --</option>
                    {INDIAN_STATES.map((st) => (
                      <option key={st.code} value={st.name}>
                        {st.name}
                      </option>
                    ))}
                  </SearchableSelect>
                  <p className="text-[11px] text-muted-foreground">Decides CGST + SGST or IGST on quotes</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="acc_terms">Usual payment terms</Label>
                  <SearchableSelect
                    id="acc_terms"
                    value={form.payment_term_id}
                    onChange={(e) => setForm((p) => ({ ...p, payment_term_id: e.target.value }))}
                    className="h-9 sm:h-10 w-full rounded-md border border-border bg-background px-2.5 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">-- Company default --</option>
                    {paymentTerms.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </SearchableSelect>
                  <p className="text-[11px] text-muted-foreground">New quotes and orders start with this</p>
                </div>
                {hasPermission("price_list.manage") && (
                  <div className="space-y-1.5">
                    <Label htmlFor="acc_pricelist">Price list</Label>
                    <SearchableSelect
                      id="acc_pricelist"
                      value={form.price_list_id}
                      onChange={(e) => setForm((p) => ({ ...p, price_list_id: e.target.value }))}
                      className="h-9 sm:h-10 w-full rounded-md border border-border bg-background px-2.5 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="">-- Catalog prices --</option>
                      {priceLists.filter((p) => p.usable_today || p.id === form.price_list_id).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </SearchableSelect>
                    <p className="text-[11px] text-muted-foreground">New quotes for this customer use these prices</p>
                  </div>
                )}
                {hasPermission("account.credit_manage") && (
                  <div className="space-y-1.5">
                    <Label htmlFor="acc_credit">Credit limit (₹)</Label>
                    <Input
                      id="acc_credit"
                      type="number"
                      min={0}
                      placeholder="No limit"
                      value={form.credit_limit}
                      onChange={(e) => setForm((p) => ({ ...p, credit_limit: e.target.value }))}
                    />
                    <p className="text-[11px] text-muted-foreground">Most we allow unpaid at one time; empty = no limit</p>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="acc_notes">Internal Notes</Label>
                <Input
                  id="acc_notes"
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => (setAddOpen(false), setEditAccount(null))}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Saving..." : editAccount ? "Save Changes" : "Create Account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteAccount} onOpenChange={(open) => !open && setDeleteAccount(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteAccount?.name}</strong>? All associated deals and contacts will be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAccount(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSubmit} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
